import { Separator } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DailySummaryListParams,
  useGetDailySummaryListQuery,
} from "~/shared/api/daily-summary/daily-summary.api";
import { BrandIcWasteField } from "@racoon/icons";
import { ProgressBar } from "~/ui/components/progressBar";
import { AnalyticsDataSection } from "~/ui/components/analyticsDataSection";
import { ArrowRight } from "lucide-react";
import type {
  AnalyticsDetailNavState,
  AnalyticsFilterValue,
  MvoAnalyticsType,
} from "./mvo-analytics-section.types";
import {
  aggregateTotalSummary,
  aggregateVisitPointStats,
  buildDetailSections,
  formatTo2,
  PAGE_TITLES,
} from "./analytics-report.shared";
import type { ReportDetailSection } from "./analytics-report.shared";
import { toDateParam } from "~/shared/utils/filterDateFormatter";
import { useDriverOnShiftsInRange } from "~/shared/hooks/use-active-truck-count.hook";

type DetailSection = ReportDetailSection;

export function MVODetailedView({
  mvoName,
  mvoId,
  isSingleDay,
  startDate,
  endDate,
  totalTrucks,
  selectedRegion,
  selectedMvo,
  returnDateFrom,
  returnDateTo,
}: {
  mvoName: string;
  mvoId: string;
  isSingleDay: boolean;
  startDate: Date;
  endDate: Date;
  totalTrucks: number;
  selectedRegion: AnalyticsFilterValue;
  selectedMvo: AnalyticsFilterValue;
  returnDateFrom?: Date;
  returnDateTo?: Date;
}) {
  const t = useT();
  const navigate = useNavigate();
  const detailNavState: AnalyticsDetailNavState = useMemo(
    () => ({
      mvoId,
      mvoName,
      startDate: toDateParam(startDate),
      endDate: toDateParam(endDate),
      isSingleDay,
      totalTrucks,
      returnState: {
        selectedRegion,
        selectedMvo,
        dateFrom: returnDateFrom ? toDateParam(returnDateFrom) : undefined,
        dateTo: returnDateTo ? toDateParam(returnDateTo) : undefined,
      },
    }),
    [
      endDate,
      isSingleDay,
      mvoId,
      mvoName,
      returnDateFrom,
      returnDateTo,
      selectedMvo,
      selectedRegion,
      startDate,
      totalTrucks,
    ],
  );
  const navigateToDetail = useCallback(
    (analyticsType: MvoAnalyticsType) => {
      navigate(`/analytics/section/${analyticsType}`, { state: detailNavState });
    },
    [detailNavState, navigate],
  );
  const queryParams: DailySummaryListParams = useMemo(
    () => ({
      owner_organization_id: mvoId,
      date_from: startDate ? toDateParam(startDate) : undefined,
      date_to: endDate ? toDateParam(endDate) : undefined,
    }),
    [mvoId, startDate, endDate],
  );

  const { data: summaryData } = useGetDailySummaryListQuery(queryParams);
  const summaryItems = useMemo(() => Object.values(summaryData ?? {}), [summaryData]);
  const totalSummary = useMemo(() => aggregateTotalSummary(summaryItems), [summaryItems]);

  const visitPointStats: Array<{ point_name?: string; volume?: number | string }> = useMemo(
    () => aggregateVisitPointStats(summaryItems),
    [summaryItems],
  );

  // Period mode only renders the recycling section, so the shift count is never shown there.
  const mvoIds = useMemo(() => [mvoId], [mvoId]);
  const { activeTrucks } = useDriverOnShiftsInRange({
    serviceProviderIds: mvoIds,
    from: startDate,
    to: endDate,
    skip: !isSingleDay,
  });

  const detailData: DetailSection[] = useMemo(
    () =>
      buildDetailSections(totalSummary, totalTrucks, visitPointStats, t, activeTrucks ?? 0),
    [totalSummary, totalTrucks, visitPointStats, t, activeTrucks],
  );

  const displayData = useMemo(
    () =>
      isSingleDay
        ? detailData
        : detailData.filter((section) => section.analyticsType === "recycling"),
    [isSingleDay, detailData],
  );

  return (
    <div className="flex-1 overflow-y-auto w-full py-4">
      <div className="space-y-6 px-4">
        {/* Download PDF Button - show only in "period" mode */}
        {!isSingleDay && (
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold text-gray-900 leading-tight">
              {t(Namespace.Govdash, "analytics.exportedVolumes") || "Вывезенные Объемы"}
            </h3>
            <div className=" flex gap-2">
              <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-2 shadow-sm flex-1">
                <div className=" rounded-xs p-1">
                  <BrandIcWasteField className="w-8 h-8" />
                </div>
                <div className="flex flex-col">
                  <p className="text-xl font-bold">
                    {totalSummary.vol_nominal_handled.toFixed(0)} м³
                  </p>
                  <p className="text-xs font-medium text-text-secondary">Вывезено</p>
                </div>
              </div>
              {/* <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-2 shadow-sm flex-1">
                <div className="bg-[#5856D6] rounded-[2px] p-1">
                  <IcWeightStation className="w-6 h-6 text-white " />
                </div>
                <div className="flex flex-col">
                  <p className="text-xl font-bold">
                    {totalNettoWeight ? totalNettoWeight / 1000 : 0} т
                  </p>
                  <p className="text-xs font-medium text-text-secondary">Захоронено</p>
                </div>
              </div> */}
            </div>
          </div>
        )}
        {displayData.map((section, index) => (
          <div key={index} className="space-y-3">
            {/* Section Title */}
            {isSingleDay ? (
              <button
                type="button"
                onClick={() => {
                  navigateToDetail(section.analyticsType);
                }}
                className="w-full flex items-center justify-between rounded-lg px-1 -mx-1 hover:bg-gray-100"
              >
                <h3 className="text-base font-semibold text-gray-900 leading-tight">
                  {PAGE_TITLES(t)[section.analyticsType]}
                </h3>
                <ArrowRight className="h-6 w-6" />
              </button>
            ) : (
              <div className="w-full flex items-center justify-between px-1 -mx-1">
                <h3 className="text-base font-semibold text-gray-900 leading-tight">
                  {PAGE_TITLES(t)[section.analyticsType]}
                </h3>
              </div>
            )}

            {/* Progress Bar */}
            {(() => {
              const progressItems = section.items
                .filter((item) => item.showInProgressBar)
                .map((item) => ({
                  color: item.badgeColor,
                  value: Number(item.value) || 0,
                }));
              const formatValue =
                section.progressBar?.formatValue === "fixed2"
                  ? (value: number) => formatTo2(value)
                  : section.progressBar?.formatValue === "round"
                    ? (value: number) => String(Math.round(value))
                    : undefined;

              return (
                <ProgressBar
                  items={progressItems}
                  valueType={section.progressBar?.valueType ?? "value"}
                  minSegmentPct={section.progressBar?.minSegmentPct}
                  minSegmentWidthPx={section.progressBar?.minSegmentWidthPx}
                  hideLabelBelowPct={section.progressBar?.hideLabelBelowPct}
                  formatValue={formatValue}
                />
              );
            })()}

            {/* Stats Grid */}
            <div
              className={`grid ${
                section.analyticsType === "stands"
                  ? section.items.length >= 4
                    ? "grid-cols-2"
                    : "grid-cols-3"
                  : "grid-cols-2"
              } gap-2 `}
            >
              {section.items.map((item, itemIndex) => (
                <AnalyticsDataSection
                  key={itemIndex}
                  badge={item.badge}
                  badgeColor={item.badgeColor}
                  subtitle={item.subtitle}
                  value={item.value}
                  label={item.label}
                />
              ))}
            </div>

            {/* Divider */}
            {index < displayData.length - 1 && <Separator className="w-full" />}
          </div>
        ))}
      </div>
    </div>
  );
}
